import { useRef, useEffect, useState } from 'react';
import { SpeakerWaveIcon, SpeakerXMarkIcon, HeartIcon, BookmarkIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon, BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import { useVideo } from '../context/VideoContext';

const VideoCard = ({ video, isActive }) => {
  const { isMuted, toggleMute, likedVideos, bookmarkedVideos, toggleLike, toggleBookmark } = useVideo();
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const isLiked = likedVideos && likedVideos[video.id];
  const isBookmarked = bookmarkedVideos && bookmarkedVideos[video.id];

  useEffect(() => {
    const videoElement = videoRef.current;
    
    if (!videoElement) return;
    
    // Reset error state when active video changes
    setError(null);
    
    let playPromise;
    if (isActive) {
      // Only reset currentTime if the video isn't already playing
      if (videoElement.paused) {
        videoElement.currentTime = 0;
      }
      
      // Reduce the play delay from 100ms to 50ms
      const playVideo = () => {
        try {
          playPromise = videoElement.play();
          
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.error('Auto-play was prevented:', error);
              setError('Video playback error: ' + error.message);
            });
          }
        } catch (e) {
          console.error('Error during play():', e);
          setError('Video playback error: ' + e.message);
        }
      };
      
      setTimeout(playVideo, 50);
    } else {
      // Only pause if there's no pending play promise
      if (videoElement.paused || !playPromise) {
        videoElement.pause();
      } else if (playPromise !== undefined) {
        playPromise
          .then(() => {
            videoElement.pause();
          })
          .catch(err => {
            console.error('Error resolving play promise:', err);
          });
      }
    }
    
    return () => {
      // Cleanup function should handle play promise properly
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            if (videoElement && !videoElement.paused) {
              videoElement.pause();
            }
          })
          .catch(() => {
            // Ignore errors during cleanup
          });
      }
    };
  }, [isActive, video.src]);

  const handleVideoError = (e) => {
    console.error('Video loading error:', e);
    setError(`Failed to load video: ${video.src}. Check browser console for details.`);
    setIsLoading(false);
  };
  
  const handleLoadedData = () => {
    console.log(`Video loaded successfully: ${video.title}`);
    setIsLoading(false);
  };

  const handleLike = () => {
    toggleLike(video.id);
  };

  const handleBookmark = () => {
    toggleBookmark(video.id);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-full w-full bg-gray-900">
        <div className="text-white text-center p-4">
          <p className="text-red-500 mb-2">Error</p>
          <p className="text-sm">{error}</p>
          <p className="text-xs mt-4">Debug info:</p>
          <p className="text-xs opacity-70">Path: {video.src}</p>
          <p className="text-xs opacity-70">ID: {video.id}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden">
      {isLoading && isActive && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-10 backdrop-blur-sm">
          <div className="w-12 h-12 rounded-full border-4 border-gray-300 border-t-pink-500 animate-spin"></div>
        </div>
      )}
      
      <video
        ref={videoRef}
        src={video.src}
        className="h-full w-full object-cover"
        loop
        playsInline
        muted={isMuted}
        onError={handleVideoError}
        onLoadedData={handleLoadedData}
        preload="auto"
      />
      
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-16 pb-6 px-5 z-20">
        <h3 className="text-white text-lg font-medium">{video.title}</h3>
        {video.description && (
          <p className="text-gray-200 text-sm mt-1.5 line-clamp-2">{video.description}</p>
        )}
      </div>
      
      <div className="absolute right-4 bottom-24 flex flex-col gap-3 z-20">
        <button 
          onClick={toggleMute}
          className="p-2.5 bg-black/30 hover:bg-black/50 rounded-full backdrop-blur-sm transition-all duration-200"
        >
          {isMuted ? (
            <SpeakerXMarkIcon className="h-5 w-5 text-white" />
          ) : (
            <SpeakerWaveIcon className="h-5 w-5 text-white" />
          )}
        </button>
        
        <button 
          onClick={handleLike}
          className="p-2.5 bg-black/30 hover:bg-black/50 rounded-full backdrop-blur-sm transition-all duration-200"
        >
          {isLiked ? (
            <HeartSolidIcon className="h-5 w-5 text-pink-500" />
          ) : (
            <HeartIcon className="h-5 w-5 text-white" />
          )}
        </button>
        
        <button 
          onClick={handleBookmark}
          className="p-2.5 bg-black/30 hover:bg-black/50 rounded-full backdrop-blur-sm transition-all duration-200"
        >
          {isBookmarked ? (
            <BookmarkSolidIcon className="h-5 w-5 text-yellow-500" />
          ) : (
            <BookmarkIcon className="h-5 w-5 text-white" />
          )}
        </button>
      </div>
    </div>
  );
};

export default VideoCard;